import { Icon } from '@features/siteStatusV2/components/icon';
import theme from '@theme';
import React from 'react';
import { Col, Container, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { FrozenKey } from './enums';
import { MapTranslationKey } from './maps';
import { TranslationNamespaces } from '../../../../locales/constants/translationNamespaces.enum';

interface EligibilityModalProps {
  isModalShown: boolean;
  setIsModalShown: React.Dispatch<React.SetStateAction<boolean>>;
  frozenInformation: Record<FrozenKey, string>;
}

const EligibilityModal: React.FC<EligibilityModalProps> = ({
  isModalShown,
  setIsModalShown,
  frozenInformation
}) => {
  const { t } = useTranslation(TranslationNamespaces.SITE_STATUS);

  const copyFrozenInformations = async () => {
    let formattedText = '';
    for (const [key, value] of Object.entries(frozenInformation)) {
      const formatedValue = value.trim() === '' ? '-' : value.trim();
      formattedText += `${t(MapTranslationKey[key as FrozenKey] ?? '')} : ${formatedValue}\n`;
    }
    await navigator.clipboard.writeText(formattedText);
  };

  return (
    <Modal show={isModalShown}>
      <StyledModalHeader>
        <InformationHeader>
          <FieldTitle>{t('buildingDetail.details')}</FieldTitle>
          <Badge>
            {t('buildingDetail.badges.eligibility.frozen.name')}
            <Icon iconName="solid-eye" width={13} height={13} style={{ marginLeft: '5px' }} />
          </Badge>
        </InformationHeader>
        <CopyButton onClick={copyFrozenInformations}>
          <DuplicateIcon iconName="duplicate" />
        </CopyButton>
      </StyledModalHeader>
      <StyledModalBody>
        <Container>
          <Row>
            <Col sm="12">
              <Row>
                <Col sm="6">
                  <FieldLabel>{t(MapTranslationKey['freezeDate'])}</FieldLabel>
                  <FieldValue>{frozenInformation.freezeDate}</FieldValue>
                </Col>
                <Col sm="6" className="mt-2">
                  <FieldLabel>{t(MapTranslationKey['unfreezeDate'])}</FieldLabel>
                  <FieldValue>{frozenInformation.unfreezeDate}</FieldValue>
                </Col>
              </Row>
            </Col>
            <Col sm="12" className="mt-2">
              <FieldLabel>{t(MapTranslationKey['freezeReason'])}</FieldLabel>
              <FieldValue>{frozenInformation.freezeReason}</FieldValue>
            </Col>
            <Col sm="12" className="mt-2">
              <FieldLabel>{t(MapTranslationKey['comments'])}</FieldLabel>
              <FieldValue>{frozenInformation.comments}</FieldValue>
            </Col>
            <Col sm="12" className="mt-2">
              <Row>
                <Col sm="6">
                  <FieldLabel>{t(MapTranslationKey['pboReference'])}</FieldLabel>
                  <FieldValue>{frozenInformation.pboReference}</FieldValue>
                </Col>
                <Col sm="6" className="mt-2">
                  <FieldLabel>{t(MapTranslationKey['freezePbo'])}</FieldLabel>
                  <FieldValue>{frozenInformation.freezePbo}</FieldValue>
                </Col>
              </Row>
            </Col>
            <Col sm="12" className="mt-2">
              <Row>
                <Col sm="6">
                  <FieldLabel>{t(MapTranslationKey['dateUnfreezePbo'])}</FieldLabel>
                  <FieldValue>{frozenInformation.dateUnfreezePbo}</FieldValue>
                </Col>
                <Col sm="6" className="mt-2">
                  <FieldLabel>{t(MapTranslationKey['freezePboReason'])}</FieldLabel>
                  <FieldValue>{frozenInformation.freezePboReason}</FieldValue>
                </Col>
              </Row>
            </Col>
          </Row>
        </Container>
      </StyledModalBody>
      <StyledModalFooter>
        <CloseButton onClick={() => setIsModalShown(false)}>
          {t('buildingDetail.close')}
        </CloseButton>
      </StyledModalFooter>
    </Modal>
  );
};

const StyledModalHeader = styled(ModalHeader)`
  display: flex;
  padding: 0.825rem 0.825rem 0.125rem;
  border-bottom: none;
  justify-content: space-between;
  align-items: center;
`;

const InformationHeader = styled.div`
  display: flex;
  justify-content: start;
  align-items: center;
`;

const CopyButton = styled.button`
  display: flex;
  width: 1.5rem;
  height: 1.5rem;
  justify-content: center;
  align-items: center;
  background-color: ${theme.palette.grey[200]};
  border-radius: 0.25rem;
  border-width: 0px;
  cursor: pointer;
  &:hover {
    background-color: ${theme.palette.grey[400]};
  }
`;

const DuplicateIcon = styled(Icon)`
  width: 1rem;
  color: ${theme.palette.grey[600]};
`;

const StyledModalBody = styled(ModalBody)`
  padding: 0.825rem 0;
`;

const StyledModalFooter = styled(ModalFooter)`
  display: flex;
  justify-content: center;
  padding: 0.125rem 0;
  background-color: ${theme.palette.grey[100]};
  border-top: 1px solid ${theme.palette.grey[200]};
`;

const CloseButton = styled.button`
  color: ${theme.palette.grey[700]};
  background-color: #fff;
  border: 1px solid ${theme.palette.grey[300]};
  font-size: 0.875rem;
  padding: 0.3rem 0.5rem;
  border-radius: 0.375rem;
  cursor: pointer;

  &:hover {
    background-color: ${theme.palette.grey[200]};
  }
`;

const FieldTitle = styled.h3`
  color: ${theme.palette.grey[700]};
  font-size: 0.875rem;
  font-weight: 500;
  margin: 0;
  padding-right: 2rem;
  position: relative;

  &::before {
    content: '';
    display: inline-block;
    width: 0.3rem;
    height: 0.3rem;
    background-color: ${theme.palette.grey[300]};
    border-radius: 50%;
    position: absolute;
    right: 15px;
    top: 5px;
  }
`;

const FieldLabel = styled.div`
  color: ${theme.palette.grey[700]};
  font-size: 0.75rem;
  font-weight: 500;
`;

const FieldValue = styled.div`
  color: ${theme.palette.grey[400]};
  font-size: 0.75rem;
  font-weight: 500;
`;

const Badge = styled.div`
  height: 1.25rem;
  position: relative;
  display: inline-flex;
  align-items: center;
  background-color: ${theme.palette.red[100]};
  color: ${theme.palette.red[500]};
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
`;

export default EligibilityModal;
